<template>
  <transition name="fadeIn">
    <div v-if="hasQuote" class="tariffWrapper">
      <div :class="['detailsWrapper', { showDetails: detailsVisible }]">
        <div class="header">
          <h4 class="productHeadline">{{ productName }}</h4>
          <button class="closeDetailsButton" @click="toggleDetails">
            <close-icon :width="20" :height="20" />
          </button>
        </div>
        <dl v-if="selectedModules">
          <template v-for="module in selectedModules">
            <dt :key="`module-${module.moduleKey}`">
              {{ $t(`modules.${module.moduleKey}.label`) }}
            </dt>
            <dd :key="`premium-${module.moduleKey}`">
              {{ swissPrice(module.premium.valueCents / 100) }}
            </dd>
          </template>
        </dl>
      </div>
      <div v-if="tariff" class="total">
        <dl class="totalPrice" @click="toggleDetails">
          <dt>{{ paymentIntervalString }}</dt>
          <dd>{{ swissPrice(tariff.totalPremium.valueCents / 100) }}</dd>
        </dl>
        <button
          v-show="!detailsVisible"
          class="openDetailsButton"
          @click="toggleDetails"
        >
          <chevron-down-icon :height="24" :width="24" />
        </button>
      </div>
    </div>
  </transition>
</template>

<script>
import ChevronDownIcon from '~/components/icons/ChevronDownIcon'
import CloseIcon from '~/components/icons/CloseIcon'

const swissNumberFormat = new Intl.NumberFormat('de-CH', {
  style: 'currency',
  currency: 'CHF',
})

export default {
  name: 'ScipSalesTariffbox',
  components: { ChevronDownIcon, CloseIcon },
  props: {
    tariff: {
      type: Object,
      default: () => ({}),
    },
    paymentInterval: {
      type: String,
      default: '',
    },
    productName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      detailsVisible: false,
    }
  },
  computed: {
    selectedModules() {
      if (this.tariff && this.tariff.premia) {
        return this.tariff.premia.filter(
          (module) => module.premium.valueCents > 0,
        )
      }

      return []
    },
    hasQuote() {
      return this.tariff?.totalPremium?.valueCents > 0
    },
    paymentIntervalString() {
      return this.$t(`policies.${this.paymentInterval}`)
    },
  },
  methods: {
    toggleDetails() {
      this.detailsVisible = !this.detailsVisible
    },

    swissPrice(number) {
      return swissNumberFormat.format(number)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@sumcumo/dextra-frontend-component-lib/dist/assets/styles/variables/colors';
@import '~@sumcumo/dextra-frontend-component-lib/dist/assets/styles/variables/typo';
@import '~@sumcumo/dextra-frontend-component-lib/dist/assets/styles/functions/px-to-rem';

$font-family-old: 'Roboto', sans-serif;
$white-clr: #fff;

.tariffWrapper {
  display: grid;
  position: fixed;
  bottom: rem(64);
  left: 0;
  padding: rem(20) rem(40) rem(8);
  width: 100%;
  grid-gap: rem(24);
  background: $white-clr;
  color: $color-paynes-grey;
  z-index: 12;

  @include media-breakpoint-up(lg) {
    position: sticky;
    top: rem(12);
    border: rem(2) dotted $color-paynes-grey;
    border-radius: rem(2);
    padding: rem(28) rem(20);
    background: transparent;
    z-index: 4;
  }
}

.header {
  display: flex;
  margin-bottom: rem(16);
  justify-content: space-between;
  align-items: center;

  @include media-breakpoint-up(lg) {
    margin-bottom: rem(24);
  }
}

.productHeadline {
  margin: 0;
  color: $color-indigo;
  font-family: $font-family-old;
  font-size: rem(24);
  line-height: rem(27);
}

dl {
  display: grid;
  margin: 0;
  grid-column-gap: rem(4);
  grid-template-columns: 1fr 1fr;
  color: $color-paynes-grey;

  @include media-breakpoint-up(lg) {
    grid-column-gap: rem(10);
    grid-template-columns: repeat(2, 1fr);
    font-size: rem(16);
    line-height: rem(24);
  }
}

.detailsWrapper {
  display: none;
  position: relative;
  @include media-breakpoint-up(lg) {
    display: block;
  }
}

.showDetails {
  display: block;
}

.closeDetailsButton,
.openDetailsButton {
  position: absolute;
  right: rem(32) * -1;
  border: none;
  background: transparent;
  color: $color-turquoise-surf;

  @include media-breakpoint-up(lg) {
    display: none;
  }
}

dt {
  grid-column: 1;
  font-weight: normal;
  white-space: nowrap;
}

dd {
  margin: 0;
  grid-column: 2;
  justify-self: end;
  font-variant-numeric: tabular-nums;
}

.total {
  display: flex;
  position: relative;
  min-height: rem(32);
  justify-content: flex-end;
  align-items: center;
}

.totalPrice {
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;

  dt {
    justify-self: end;
  }

  dd {
    color: $color-turquoise-surf;
    font-family: $font-family-old;
    font-weight: bold;
    font-size: clamp(#{rem(20)}, calc(0.4vw + 1rem), #{rem(30)});
    line-height: clamp(#{rem(24)}, calc(0.5vw + 1rem), #{rem(36)});
  }
}

.fadeIn-enter-active,
.fadeIn-leave-active {
  transform: translateY(0);
  transition: opacity 300ms ease-in, transform 300ms ease-in;
}

.fadeIn-enter,
.fadeIn-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>
